import React from 'react';
import { Handle, Position } from 'reactflow';
import { Mail, MessageSquare, Gift, Settings, Plus, UserCog } from 'lucide-react';
import { AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface ActionNodeV2Props {
  data: FlowNodeData;
}

const getActionIcon = (subType?: AutomationActionNodeSubType) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      return <Mail className="h-4 w-4" />;
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return <Gift className="h-4 w-4" />;
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return <MessageSquare className="h-4 w-4" />;
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      return <UserCog className="h-4 w-4" />;
    default:
      return <Settings className="h-4 w-4" />;
  }
};

const getActionLabel = (subType?: AutomationActionNodeSubType) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      return 'Send Email';
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return 'Apply Boost';
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return 'Cashback Boost';
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      return 'Update Member Data';
    default:
      return 'Action';
  }
};

const getActionDescription = (subType?: AutomationActionNodeSubType, metadata?: any, actionOptions?: any[]) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      if (metadata?.templateId && actionOptions?.length > 0) {
        // Find template name from actionOptions
        const template = actionOptions[0]?.messageTemplates?.find((t: any) => t.id === metadata.templateId);
        return template?.displayName || 'Email template configured';
      }
      return 'Send an email to the member';
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return metadata?.boostId ? `Boost ID: ${metadata.boostId}` : 'Apply performance boost';
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return 'Apply cashback boost to member';
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      if (metadata?.type && metadata?.fieldValue) {
        const actionType = metadata.type === 'TAG' ? 'Add Tag' : 'Add to Group';
        return `${actionType}: ${metadata.fieldValue}`;
      }
      return 'Update member data';
    default:
      return 'Perform an action';
  }
};

const renderEmailDetails = (metadata: any, actionOptions?: any[]) => {
  if (!metadata) return null;

  const getSenderInfo = () => {
    if (metadata.fromPreviousSender) {
      return 'Previous Sender';
    }
    if (metadata.resourceId && actionOptions?.length > 0) {
      const resource = actionOptions[0]?.emailResources?.find((r: any) => r.id === metadata.resourceId);
      if (resource) {
        // The displayName might already contain the email or proper name
        // Check for email-specific properties first, then fallback to displayName
        const emailAddress = resource.email || resource.emailAddress || resource.address || resource.sender;
        const displayName = resource.displayName || resource.name;

        // If we have an explicit email address, show it
        if (emailAddress && emailAddress !== displayName) {
          return emailAddress;
        }

        // Otherwise use displayName (which might already be the email)
        return displayName || `Resource ${metadata.resourceId}`;
      }
    }
    return 'Unspecified';
  };

  const getTemplateInfo = () => {
    if (metadata.templateId && actionOptions?.length > 0) {
      const template = actionOptions[0]?.messageTemplates?.find((t: any) => t.id === metadata.templateId);
      return template?.displayName || `Template ${metadata.templateId}`;
    }
    return 'No template selected';
  };

  return (
    <div className="space-y-1">
      <p className="text-xs text-grey-5">
        <span className="font-medium">{getTemplateInfo()}</span>
      </p>
      <p className="text-xs text-grey-4">
        From: <span className="font-medium">{getSenderInfo()}</span>
      </p>
    </div>
  );
};

const renderUpdateMemberDataDetails = (metadata: any) => {
  if (!metadata || !metadata.type || !metadata.fieldValue) return null;

  const actionType = metadata.type === 'TAG' ? 'Tag' : 'Group';
  const actionVerb = metadata.type === 'TAG' ? 'Add tag' : 'Add to group';

  return (
    <div className="space-y-1">
      <p className="text-xs text-grey-5">
        <span className="font-medium">{actionVerb}</span>
      </p>
      <p className="text-xs text-grey-4">
        {actionType}: <span className="font-medium">{metadata.fieldValue}</span>
      </p>
    </div>
  );
};

export const ActionNodeV2: React.FC<ActionNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    subType,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
    actionOptions,
  } = data;

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteNode?.(nodeId);
  };

  const actionSubType = subType as AutomationActionNodeSubType;
  const isConfigured = metadata && (
    (actionSubType === AutomationActionNodeSubType.SEND_EMAIL && metadata.templateId && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && metadata.boostId && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && metadata.type && metadata.fieldValue)
  );

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container - Figma style */}
      <div
        className={cn(
          'w-64 bg-white border border-gray-200 rounded-2xl shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300',
          isSelected && 'ring-2 ring-blue-500 ring-offset-2 border-blue-300'
        )}
        onClick={handleNodeClick}
      >
        <div className="p-4">
          {/* Header with icon and title */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <div className="text-white">
                  {getActionIcon(subType as AutomationActionNodeSubType)}
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-sm">{getActionLabel(subType as AutomationActionNodeSubType)}</h3>
              </div>
            </div>

            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-gray-100"
                onClick={handleDelete}
              >
                <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>

          {/* Content area */}
          <div className="space-y-3">
            {isConfigured ? (
              <div className="space-y-2">
                <div className="bg-green-50 rounded-lg px-3 py-2">
                  <span className="text-sm text-green-700 font-medium">Configured</span>
                  <p className="text-xs text-green-600 mt-1">
                    {getActionDescription(subType as AutomationActionNodeSubType, metadata, actionOptions)}
                  </p>
                </div>

                {/* Action-specific details */}
                {actionSubType === AutomationActionNodeSubType.SEND_EMAIL && metadata && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    {renderEmailDetails(metadata, actionOptions)}
                  </div>
                )}
                {actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && metadata?.boostId && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-600">Boost ID: {metadata.boostId}</p>
                  </div>
                )}
                {actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && metadata && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    {renderUpdateMemberDataDetails(metadata)}
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-transparent hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNodeClick();
                }}
              >
                <Plus className="h-3 w-3 mr-2" />
                Configure Action
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Add button positioned at bottom center */}
      {isEditing && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 z-20">
          <Button
            variant="outline"
            size="sm"
            className="w-8 h-8 p-0 rounded-full bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 shadow-md"
            onClick={(e) => {
              e.stopPropagation(); // Prevent node selection
              const rect = e.currentTarget.getBoundingClientRect();
              data.onShowOptionBar?.(nodeId, {
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
          >
            <Plus className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      )}
    </div>
  );
};
